import { Injectable, Signal, signal } from '@angular/core';

/**
 * Сервіс для читання параметрів що передає нативний додаток через URL
 * при відкритті webView.
 *
 * Оскільки застосунок використовує HashLocation (`withHashLocation()`),
 * Angular під час initial navigation перезаписує `window.location` і
 * прибирає search-частину (`?electro=1&token=abc`).
 *
 * Рішення: `main.ts` зберігає `window.location.search` у `sessionStorage`
 * ДО виклику `bootstrapApplication`. Цей сервіс читає збережені параметри
 * звідти. Завдяки `sessionStorage` параметри живуть протягом усієї сесії
 * вкладки (в тому числі при hot-reload під час розробки).
 *
 * Пріоритет джерел:
 *   1. `sessionStorage['webview_params']`  ← збережено main.ts до bootstrap
 *   2. `window.location.search`            ← fallback (prod, якщо читаємо до навігації)
 */
@Injectable({ providedIn: 'root' })
export class WebViewParamsService {
  private readonly params: URLSearchParams;

  /**
   * `true` якщо транспортний засіб є електричним (`?electro=1`).
   * `false` при `?electro=0` або відсутньому параметрі.
   */
  readonly isElectro: Signal<boolean>;

  /**
   * `true` якщо WebView відкрито для завершення зміни (`?FINISH_WORKING=1`).
   */
  readonly isFinishWorking: Signal<boolean>;

  constructor() {
    const stored = sessionStorage.getItem('webview_params');
    const search = stored ?? window.location.search;
    this.params = new URLSearchParams(search);
    this.isElectro = signal(this.params.get('electro') === '1').asReadonly();
    this.isFinishWorking = signal(this.params.get('FINISH_WORKING') === '1').asReadonly();
  }

  /**
   * Повертає довільний рядковий параметр з webView URL.
   * @example webViewParamsService.get('token')
   */
  get(key: string): string | null {
    return this.params.get(key);
  }
}
